import React from 'react';
import { Target } from 'lucide-react';
import type { ProgrammeReadModel, ValueProvenModel } from '../utils/programmeModel';
import SetupPanel from './SetupPanel';
import type { SetupItems, SetupFacts } from './SetupPanel';
import HeadlineMeasures from './HeadlineMeasures';
import ValueBands from './ValueBands';
import ValueStreamTable from './ValueStreamTable';
import AttentionList from './AttentionList';
import CalibrationView from './CalibrationView';
import { readOrgContext } from '../utils/orgContext';
import { readVision } from '../utils/vision';
import { readScope } from '../utils/scope';
import { getEngagementSnapshot } from '../hooks/useEngagement';
import '../styles/Dashboard.css';

// Dashboard — the organisation-centric programme home (007-dashboard-shell),
// REBUILT for 029-value-proven-dashboard (Increment F) into the five-element
// VALUE-PROVEN board. A PURE view: App.tsx computes BOTH deriveProgramme(facts)
// and deriveValueProven(facts) and passes them in; the Dashboard derives and
// fetches nothing.
//
// Leading state (FR-012): setup-completeness is read from the EXISTING setup
// checklist (`setup-org`/`setup-vision`/`setup-areas`) — no second derivation.
//   • Setup-INCOMPLETE → the SetupPanel LEADS (its three-row to-do checklist);
//     the value board is ABSENT (the chosen default — no teaser).
//   • Setup-COMPLETE → the SetupPanel renders its `allDone` revisit strip,
//     FOLLOWED by the value-proven board.
//
// Reserved-colour discipline: blue (--ap-brand) is the everyday primary; CORAL
// (--ap-accent) is the ONE reserved accent — on this board it is spent only on
// the single urgent attention item (data-reserved-colour="urgent").

export type DashboardProps = {
  state: 'loading' | 'error' | 'ready';
  /** Required when state === 'ready'. Drives setup-completeness + the goal. */
  model?: ProgrammeReadModel;
  /** The value board's data (App always passes it for the ready state). */
  valueProven?: ValueProvenModel;
  /** Optional message for the error state. */
  error?: string;
};

// ── Goal eyebrow: named-goal hero block (023, FR-001; 024, FR-007) ────────
// The programme goal, in the sponsor's own words (seeded from the CONFIRMED
// Vision Cover Story headline). THREE states:
//   • CONFIRMED  (goal present) → the quoted headline hero (programme-goal).
//   • PENDING    (goalsCapturedUnconfirmed) → "Confirm your Cover Story headline"
//                — the honest "not confirmed yet" wording; NO headline is
//                ever elevated as the goal.
//   • NO-VISION  (no goal, not pending) → "Name the goal".
// No goal is ever fabricated. Since the leading-state rule (029 F) the board —
// and so this !goal branch — only renders on a setup-complete engagement, and
// since the 054 T052 cutover the legacy vision route is gone (the facilitated
// session is session-keyed, not bare-linkable), so the empty wording is plain
// text, not a link (cutover-ruling.md, Ruling 3 detector 5: honest absence
// beats a dead link).
const GoalEyebrow: React.FC<{
  goal: ProgrammeReadModel['goal'];
  pending: boolean;
}> = ({ goal, pending }) => {
  if (!goal) {
    return (
      <div className="ap-dash-goal">
        <div className="ap-dash-goal-eyebrow">
          <Target size={15} aria-hidden="true" /> Goal
        </div>
        <span className="ap-dash-goal-name ap-dash-goal-name--empty">
          {pending ? 'Confirm your Cover Story headline' : 'Name the goal'}
        </span>
      </div>
    );
  }
  // The goal hero is presentational duplication of the same sponsor words the
  // vision panel shows (both reference model.vision.coverStory); tests scope to
  // this hero (data-testid) vs the vision panel rather than the markup contorting.
  return (
    <div className="ap-dash-goal" data-testid="programme-goal">
      <div className="ap-dash-goal-eyebrow">
        <Target size={15} aria-hidden="true" /> Goal &middot; in the sponsor&rsquo;s words
      </div>
      <p className="ap-dash-goal-name">&ldquo;{goal.headline}&rdquo;</p>
    </div>
  );
};

// ── Setup-panel props (028 E1) ───────────────────────────────────────────
// The "Set up this engagement" panel is a THIN, prop-driven view (FR-009): the
// dashboard derives its props here — `items` from the EXISTING `programmeModel`
// setup checklist (never a second completeness derivation), `facts` from the
// synchronous snapshot readers (the same provider snapshot the route already
// resolved — no fetch). The panel recomputes nothing; honesty is structural.
function deriveSetupItems(model: ProgrammeReadModel): SetupItems {
  const stateOf = (id: string): 'done' | 'todo' => {
    const item = model.checklist.find((i) => i.phase === 'setup' && i.id === id);
    return item && item.state === 'done' ? 'done' : 'todo';
  };
  return {
    // 032: the REQUIRED knowledge-repository step, read from the SAME setup
    // checklist (never a second derivation). Done iff its item reads `done`.
    'setup-repo': stateOf('setup-repo'),
    'setup-org': stateOf('setup-org'),
    'setup-vision': stateOf('setup-vision'),
    'setup-areas': stateOf('setup-areas'),
  };
}

// The (non-secret) knowledge-repo link descriptor, read over the SAME provider
// snapshot the other readers use (032). Absent/unlinked → null owner/repo, so the
// repo row's live summary is shown ONLY when the repo is genuinely linked.
function readRepoLinkFacts(): { repoOwner: string | null; repoName: string | null } {
  const repoLink = getEngagementSnapshot()?.repoLink as
    | { status?: string; owner?: string; org?: string; repo?: string }
    | undefined;
  if (!repoLink || repoLink.status !== 'linked') return { repoOwner: null, repoName: null };
  return {
    repoOwner: repoLink.owner ?? repoLink.org ?? null,
    repoName: repoLink.repo ?? null,
  };
}

function deriveSetupFacts(): SetupFacts {
  const org = readOrgContext();
  const vision = readVision();
  const areas = readScope().areas;
  return {
    orgName: org.organisation?.name ?? null,
    goalCount: vision.goals.length,
    coverStoryConfirmed: vision.coverStory?.confirmed === true,
    // Candidate value streams (the unit a later loop attaches to) — the same
    // `streams` sub-doc the readScope adapter still surfaces as `areas`.
    streamCount: areas.length,
    // The linked knowledge-repo owner/repo for the done repo row's summary (032).
    ...readRepoLinkFacts(),
  };
}

// Setup-complete ⇔ all three setup checklist items read `done` (FR-012). This is
// the SAME source SetupPanel/deriveSetupItems use — never a second derivation.
function isSetupComplete(items: SetupItems): boolean {
  return (
    items['setup-repo'] === 'done' &&
    items['setup-org'] === 'done' &&
    items['setup-vision'] === 'done' &&
    items['setup-areas'] === 'done'
  );
}

// ── The value-proven board (029 F) ────────────────────────────────────────
const ValueProvenBoard: React.FC<{
  model: ProgrammeReadModel;
  valueProven: ValueProvenModel;
}> = ({ model, valueProven }) => (
  <div className="ap-dash-board" data-testid="value-proven-board">
    {/* (a) The named goal, in the sponsor's own words. */}
    <GoalEyebrow goal={model.goal} pending={model.goalsCapturedUnconfirmed === true} />
    {/* (b) The three headline measures (progress-to-vision REAL). */}
    <HeadlineMeasures measures={valueProven.measures} />
    {/* (c) The three value bands (Proven honest empty; expected/speculative). */}
    <ValueBands bands={valueProven.bands} />
    {/* (d) The value-stream table (REAL rows from the areas). */}
    <ValueStreamTable rows={valueProven.streamRows} />
    {/* (e) Footer: the attention list + the calibration view. */}
    <div className="ap-dash-board-footer">
      <AttentionList items={valueProven.attention} />
      <CalibrationView points={valueProven.calibration} />
    </div>
  </div>
);

// ── Ready dashboard ───────────────────────────────────────────────────────
const DashboardReady: React.FC<{
  model: ProgrammeReadModel;
  valueProven?: ValueProvenModel;
}> = ({ model, valueProven }) => {
  const items = deriveSetupItems(model);
  const setupComplete = isSetupComplete(items);

  return (
    <div className="ap-dash">
      {/* The setup panel leads ALWAYS: its three-row to-do checklist while
          setup is incomplete, or its `allDone` revisit strip once complete. */}
      <SetupPanel items={items} facts={deriveSetupFacts()} />

      {/* The value-proven board leads only once setup is COMPLETE (FR-012). */}
      {setupComplete && valueProven ? (
        <ValueProvenBoard model={model} valueProven={valueProven} />
      ) : null}
    </div>
  );
};

const Dashboard: React.FC<DashboardProps> = ({ state, model, valueProven, error }) => {
  if (state === 'loading') {
    return (
      <div className="ap-dash ap-dash--loading">
        <p className="ap-dash-loading" data-testid="dashboard-loading">
          Loading the programme&hellip;
        </p>
      </div>
    );
  }

  if (state === 'error' || !model) {
    return (
      <div className="ap-dash ap-dash--error">
        <p className="ap-dash-error" role="alert">
          {error || 'Could not load the programme.'}
        </p>
      </div>
    );
  }

  return <DashboardReady model={model} valueProven={valueProven} />;
};

export default Dashboard;
